import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import type {
  SubAgentSessionAvailability,
} from "@/hooks/use-sub-agent-session-state";
import type { SubAgentThreadState } from "@/lib/sub-agent-thread";

const LABEL_KEYS: Record<SubAgentThreadState, string> = {
  queued: "subAgent.state.queued",
  running: "subAgent.state.working",
  working: "subAgent.state.working",
  paused: "subAgent.state.paused",
  completed: "subAgent.state.completed",
  failed: "subAgent.state.failed",
  cancelled: "subAgent.state.cancelled",
  timed_out: "subAgent.state.timedOut",
};

export interface SubAgentStateBadgeProps {
  state: SubAgentThreadState;
  availability?: SubAgentSessionAvailability;
  staleEmpty?: boolean;
  /** Pre-handle lifecycle shown while the spawn tool itself is still running. */
  starting?: boolean;
  className?: string;
}

export function SubAgentStateBadge({
  state,
  availability = "available",
  staleEmpty = false,
  starting = false,
  className,
}: SubAgentStateBadgeProps) {
  const { t } = useTranslation();
  const unavailable = availability !== "available";
  const label = unavailable
    ? t("subAgent.state.unavailable")
    : staleEmpty
      ? t("subAgent.state.noOutputYet")
      : starting
        ? t("subAgent.state.starting")
        : t(LABEL_KEYS[state]);

  return (
    <Badge
      asChild
      variant="outline"
      className={cn(
        "gap-1 px-1.5 py-0.5 font-medium",
        unavailable || staleEmpty
          ? "border-muted-foreground/25 bg-muted text-muted-foreground"
          : state === "completed"
            ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
            : state === "failed" || state === "timed_out"
              ? "border-destructive/25 bg-destructive/10 text-destructive"
              : state === "cancelled"
                ? "border-muted-foreground/25 bg-muted text-muted-foreground"
                : "border-blue-500/25 bg-blue-500/10 text-blue-700 dark:text-blue-300",
        className,
      )}
    >
      <span data-testid="sub-agent-state-badge">{label}</span>
    </Badge>
  );
}
